import { defineStore } from 'pinia'
import { ref, reactive } from 'vue'
import { useJobEvents } from '@/composables/jobEvents'
import { useToastStore } from './toastStore'

const STORE_NAME = 'jobs'
const ACTIVE_STATES = ['Queued', 'Processing', 'Enqueued', 'Running', 'Awaiting', 'Calculating', 'Initializing', 'Aggregating', 'Saving']
const baseUri = process.env.VUE_APP_API_URL

const MAX_HISTORY_EVENTS = 50

export const useJobStore = defineStore(STORE_NAME, () => {
    const jobs = ref([])
    const eventHistory = ref([])
    const heartbeatReceived = ref(false)
    const lastHeartbeat = ref(null)
    const callbacks = new Map()
    const toastStore = useToastStore()

    let jobEvents = null
    let sseInitialized = false
    let suppressToasts = false

    function addEventToHistory(eventType, data) {
        const event = {
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            eventType,
            timestamp: new Date(),
            jobId: data.jobId,
            description: data.metadata?.friendlyName || data.description || 'Job',
            state: data.state,
            details: data.metadata?.details || data.details || null,
            errorMessage: data.errorMessage || null
        }
        eventHistory.value.unshift(event)
        // Keep only the last N events
        if (eventHistory.value.length > MAX_HISTORY_EVENTS) {
            eventHistory.value = eventHistory.value.slice(0, MAX_HISTORY_EVENTS)
        }
    }

    function handleConnected(data) {
        heartbeatReceived.value = true
        lastHeartbeat.value = new Date()
    }

    // Verify session is still valid before connecting SSE
    async function verifyAuth() {
        try {
            const res = await fetch(`${baseUri}/auth/me`, {
                method: 'GET',
                credentials: 'include'
            })
            return res.ok
        } catch {
            return false
        }
    }

    function formatDetails(details) {
        if (!details || typeof details !== 'object') {
            return ''
        }
        const parts = []
        for (const [key, val] of Object.entries(details)) {
            parts.push(`${key}: ${val}`)
        }
        return parts.length > 0 ? ` - ${parts.join(', ')}` : ''
    }

    function handleJobStarted(data) {
        // Add to history (skip during replay)
        if (!suppressToasts) {
            addEventToHistory('job.started', data)
        }

        // Use loose equality to handle string/number jobId mismatches
        const existingJob = jobs.value.find(j => j.id == data.jobId)
        if (existingJob) {
            existingJob.state = data.state
            if (data.metadata) {
                existingJob.description = data.metadata.friendlyName || existingJob.description
                existingJob.details = data.metadata.details || existingJob.details
            }
            // Don't show toast for updates to existing jobs
            return
        }

        const job = reactive({
            id: data.jobId,
            description: data.metadata?.friendlyName || 'Job',
            state: data.state || 'Processing',
            details: data.metadata?.details || null
        })
        jobs.value.push(job)

        // Only show toast for new jobs, not replayed ones
        if (!suppressToasts) {
            const description = data.metadata?.friendlyName || 'Job'
            const details = data.metadata?.details || null
            toastStore.addToast(`${description} Started${formatDetails(details)}`)
        }
    }

    function handleJobProgress(data) {
        // Use loose equality to handle string/number jobId mismatches
        const job = jobs.value.find(j => j.id == data.jobId)
        if (job) {
            job.state = data.state
            if (data.metadata) {
                if (data.metadata.friendlyName) {
                    job.description = data.metadata.friendlyName
                }
                if (data.metadata.details) {
                    job.details = data.metadata.details
                }
            }
        }
    }

    function handleJobCompleted(data) {
        // Use loose equality to handle string/number jobId mismatches
        const job = jobs.value.find(j => j.id == data.jobId)

        // Add to history with job details
        addEventToHistory('job.completed', {
            ...data,
            description: job?.description,
            details: job?.details
        })

        if (job) {
            job.state = data.state || 'Succeeded'

            const description = job.description || 'Job'
            toastStore.addToast(`${description} Completed${formatDetails(job.details)}`)

            const cb = callbacks.get(job.id)
            if (cb) {
                cb(job.id, job.state)
                callbacks.delete(job.id)
            }
        }

        // Remove completed job from list
        jobs.value = jobs.value.filter(j => ACTIVE_STATES.includes(j.state))
    }

    function handleJobFailed(data) {
        // Use loose equality to handle string/number jobId mismatches
        const job = jobs.value.find(j => j.id == data.jobId)
        const description = job?.description || 'Job'
        const errorMessage = data.errorMessage || 'Unknown error'

        // Add to history with job details
        addEventToHistory('job.failed', {
            ...data,
            description: job?.description,
            details: job?.details
        })

        toastStore.addToast(`${description} Failed: ${errorMessage}`)

        if (job) {
            job.state = 'Failed'

            const cb = callbacks.get(job.id)
            if (cb) {
                cb(job.id, 'Failed')
                callbacks.delete(job.id)
            }
        }

        // Remove failed job from list
        jobs.value = jobs.value.filter(j => ACTIVE_STATES.includes(j.state))
    }

    async function initializeSSE() {
        if (sseInitialized) {
            return
        }

        // Verify auth before connecting - prevents SSE attempts with stale cookies
        const isAuthenticated = await verifyAuth()
        if (!isAuthenticated) {
            return
        }

        // Clear any stale jobs from persisted state - the server will replay active jobs on connect
        jobs.value = []
        heartbeatReceived.value = false
        lastHeartbeat.value = null

        // Suppress toasts briefly during initial connection to avoid showing toasts for replayed jobs
        suppressToasts = true
        setTimeout(() => {
            suppressToasts = false
        }, 2000) // Allow 2 seconds for replay to complete

        jobEvents = useJobEvents()
        jobEvents.initialize({
            onConnected: handleConnected,
            onJobStarted: handleJobStarted,
            onJobProgress: handleJobProgress,
            onJobCompleted: handleJobCompleted,
            onJobFailed: handleJobFailed
        })

        sseInitialized = true
    }

    function disconnectSSE() {
        if (jobEvents) {
            jobEvents.disconnect()
            sseInitialized = false
            jobEvents = null
        }
    }

    function addJob(id, description, onComplete) {
        // Normalize id to string to match SSE event format
        const jobId = String(id)

        // Check if job was already added by SSE
        const existingJob = jobs.value.find(j => j.id == jobId)
        if (existingJob) {
            existingJob.description = description || existingJob.description
        } else {
            // Job not yet added by SSE - add it locally
            const job = reactive({
                id: jobId,
                description: description || 'Job',
                state: 'Queued',
                details: null
            })
            jobs.value.push(job)
        }

        if (onComplete) {
            callbacks.set(jobId, onComplete)
        }
    }

    const activeJobs = () => jobs.value.filter(j => ACTIVE_STATES.includes(j.state))

    const isConnected = () => jobEvents?.isConnected?.value ?? false

    function clearHistory() {
        eventHistory.value = []
    }

    return {
        jobs,
        eventHistory,
        heartbeatReceived,
        lastHeartbeat,
        addJob,
        activeJobs,
        initializeSSE,
        disconnectSSE,
        isConnected,
        clearHistory
    }
}, { persist: true })
